import { useTheme } from "@/src/styles/theme-provider";
import { youtubeWatchUrl } from "@/src/utils/link-preview-providers.utils";
import { Play, VideoSquare } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Linking, Pressable, Text, View } from "react-native";
import PreviewDismissButton from "./dismiss-button";
import PreviewFooter from "./preview-footer";
import PreviewImage from "./preview-image";
import type { PreviewCardProps } from "./webpage-preview";

/**
 * Video card for a YouTube link: thumbnail, play overlay, title, description and
 * the favicon/site footer (as in the Selisian app).
 *
 * Pressing it hands the video to the system — the YouTube app on native, a new
 * tab on web — which is what `Linking.openURL` already does for every other
 * link in a post. The card links to the canonical watch URL rather than the
 * address as typed, so `youtu.be` and `/embed/` links land on the same page.
 */
const YoutubePreview = ({ preview, compact, onDismiss }: PreviewCardProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const slate = themeVars["--color-slate-default"] as string;
  const target = preview.videoId ? youtubeWatchUrl(preview.videoId) : preview.url;

  return (
    <Pressable
      onPress={() => {
        Linking.openURL(target).catch(() => {
          // A blocked pop-up or a missing YouTube app is not worth a crash.
        });
      }}
      className="mx-4 mb-2 border border-ash-3 rounded-xl overflow-hidden bg-chalk hover:bg-ash-3 active:opacity-80"
      testID="link-preview-card"
      accessibilityRole="link"
      accessibilityLabel={preview.title ?? t("LABEL_PLAY_VIDEO")}
    >
      <View className="relative">
        {/* YouTube thumbnails are 16:9 (the composer uses the shorter card). */}
        <PreviewImage
          uri={preview.imageUrl}
          aspectRatio={compact ? 2.4 : 16 / 9}
          testID="link-preview-image"
          fallback={<VideoSquare size={24} color={slate} />}
        />
        {preview.imageUrl ? (
          <View
            pointerEvents="none"
            className="absolute inset-0 items-center justify-center"
            testID="link-preview-play-overlay"
          >
            <View className="w-14 h-14 rounded-full bg-black/60 items-center justify-center">
              <Play size={28} variant="Bold" color="white" />
            </View>
          </View>
        ) : null}
      </View>
      <View className="px-3 pt-2.5 pb-3 gap-1">
        {preview.title ? (
          <Text className="text-sm font-semibold text-onyx" numberOfLines={2}>
            {preview.title}
          </Text>
        ) : null}
        {preview.description ? (
          <Text className="text-xs text-slate-default" numberOfLines={2}>
            {preview.description}
          </Text>
        ) : null}
        <View className="mt-0.5">
          <PreviewFooter preview={preview} iconColor={slate} />
        </View>
      </View>
      {onDismiss ? <PreviewDismissButton onPress={onDismiss} /> : null}
    </Pressable>
  );
};

export default YoutubePreview;
